// Public landing page at / for unauthenticated visitors. Carrier verification
// (Twilio A2P) requires the business description, SMS program details, and
// policy links to be reachable with no login — this page fronts the login flow.
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <div className="relative min-h-svh flex items-center overflow-hidden">
        <img
          src="/landing-hero.jpg"
          alt="A permit held in a red Kodiak net over clear flats water"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950/40 via-transparent to-transparent" />

        <div className="relative w-full max-w-6xl mx-auto px-4 py-16">
          <div
            className="max-w-xl bg-bear/85 backdrop-blur-sm p-8 sm:p-10 space-y-5
              animate-[fade-up_.7s_ease_both]"
          >
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/70 uppercase">
              Citizen Science · Fisheries Data
            </p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-widest text-white uppercase leading-none">
              Kodiak Labs
            </h1>
            <p className="text-white/90 text-[15px] leading-relaxed [animation-delay:150ms] animate-[fade-up_.7s_ease_both]">
              Founded by industry veterans and funded by Accomplice, Kodiak
              Labs will empower a citizen science movement where data collected
              across recreational fisheries is leveraged by conservationists
              working to sustain the health of our natural resources.
            </p>
            <div className="flex flex-wrap gap-3 pt-1 [animation-delay:350ms] animate-[fade-up_.7s_ease_both]">
              <a
                href="#who-we-are"
                className="px-6 py-2.5 rounded-xl bg-white text-bear
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-stone-100 transition-colors"
              >
                Learn more
              </a>
              <a
                href="#login"
                className="px-6 py-2.5 rounded-xl border border-white/70 text-white
                  font-mono text-[11px] tracking-[0.15em] uppercase font-semibold
                  hover:bg-white/10 transition-colors"
              >
                Sign in
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Who we are + policy links ─────────────────────────────────── */}
      <div id="who-we-are" className="max-w-6xl mx-auto px-4 py-16 space-y-10 scroll-mt-8">
        <div className="max-w-xl">
          <h2 className="font-display font-black text-2xl tracking-widest text-bear uppercase mb-3">
            Who we are
          </h2>
          <p className="text-sm leading-relaxed text-stone-700">
            Kodiak Labs is a registered trade name of Net Don't Lie
            Enterprises, Inc. We build a smart fishing net for sport
            anglers: the net's onboard hardware records the weight, length,
            location, and photos of each catch, and anglers review their
            catch data in this web app.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              label: 'About',
              href: '/legal#about',
              body: 'Company details and how to reach us: info@kodiaklabs.com.',
            },
            {
              label: 'Privacy Policy',
              href: '/legal#privacy',
              body: 'What we collect and how we use it. We never share or sell mobile numbers.',
            },
            {
              label: 'Terms of Service',
              href: '/legal#terms',
              body: 'The terms of the Kodiak service.',
            },
            {
              label: 'SMS Policy',
              href: '/legal#sms-program',
              body: 'Opted-in users receive account notifications, event alerts, and support. Reply STOP to opt out. Message & data rates may apply.',
            },
          ].map(({ label, href, body }) => (
            <a
              key={label}
              href={href}
              className="group bg-white rounded-2xl border border-stone-200 p-6
                hover:border-bear/50 transition-colors"
            >
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-bear mb-2">
                {label}{' '}
                <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
              </p>
              <p className="text-sm leading-relaxed text-stone-700">{body}</p>
            </a>
          ))}
        </div>

        <p className="font-mono text-[10px] text-stone-400 border-t border-stone-200 pt-6">
          © 2026 Net Don't Lie Enterprises, Inc. d/b/a Kodiak Labs
        </p>
      </div>
    </div>
  )
}
